import { useState, useEffect, useRef, useCallback } from 'react'
import {
  ChevronLeft,
  Search,
  Filter,
  ChevronDown,
  X,
  Mail,
  Webhook,
  FileSpreadsheet,
  Sparkles,
  Calendar,
  Inbox,
  Upload,
  MoreVertical,
  Eye,
  Flag,
  Trash2,
  FileText,
  AlertTriangle,
} from 'lucide-react'
import type { ItemsListProps, Item, SourceType, ReportStatus, GemFilter, FilterParams } from './types'
import { Pagination } from '../ui/Pagination'

interface FilterState {
  streamId: string | null
  sourceType: SourceType | null
  gemStatus: GemFilter
  reportStatus: ReportStatus | null
  dateFrom: string | null
  dateTo: string | null
  search: string
}

// Helper to parse URL params into filter state
function getFiltersFromUrl(currentStreamId?: string): FilterState {
  if (typeof window === 'undefined') {
    return {
      streamId: currentStreamId || null,
      sourceType: null,
      gemStatus: 'all',
      reportStatus: null,
      dateFrom: null,
      dateTo: null,
      search: '',
    }
  }

  const params = new URLSearchParams(window.location.search)
  return {
    streamId: params.get('stream') || currentStreamId || null,
    sourceType: (params.get('source') as SourceType) || null,
    gemStatus: (params.get('gem_status') as GemFilter) || 'all',
    reportStatus: (params.get('report_status') as ReportStatus) || null,
    dateFrom: params.get('from') || null,
    dateTo: params.get('to') || null,
    search: params.get('q') || '',
  }
}

// Convert FilterState to FilterParams for callbacks
function toFilterParams(filters: FilterState): FilterParams {
  return {
    streamId: filters.streamId,
    sourceType: filters.sourceType,
    gemStatus: filters.gemStatus,
    reportStatus: filters.reportStatus,
    dateFrom: filters.dateFrom,
    dateTo: filters.dateTo,
    search: filters.search,
  }
}

export function ItemsList({
  items,
  streams,
  currentStream,
  pagination,
  onView,
  onFlagAsGem,
  onUnflagGem,
  onDelete,
  onBack,
  onUploadCsv,
  onPageChange,
  onFilterChange,
}: ItemsListProps) {
  const [filters, setFilters] = useState<FilterState>(() =>
    getFiltersFromUrl(currentStream?.id)
  )

  // Show filters if any URL params are set
  const [showFilters, setShowFilters] = useState(() => {
    const initialFilters = getFiltersFromUrl(currentStream?.id)
    return !!(
      (!currentStream && initialFilters.streamId) ||
      initialFilters.sourceType ||
      initialFilters.gemStatus !== 'all' ||
      initialFilters.reportStatus ||
      initialFilters.dateFrom ||
      initialFilters.dateTo
    )
  })

  // Debounce timer for search
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Apply filter changes to server (debounced for search)
  const applyFilters = useCallback((newFilters: FilterState, immediate = false) => {
    if (onFilterChange) {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }

      if (immediate || !newFilters.search) {
        onFilterChange(toFilterParams(newFilters))
      } else {
        // Debounce search to avoid too many requests
        searchTimeoutRef.current = setTimeout(() => {
          onFilterChange(toFilterParams(newFilters))
        }, 300)
      }
    }
  }, [onFilterChange])

  // Update filters and trigger server request
  const updateFilters = useCallback((updater: (prev: FilterState) => FilterState, immediate = false) => {
    setFilters(prev => {
      const newFilters = updater(prev)
      applyFilters(newFilters, immediate)
      return newFilters
    })
  }, [applyFilters])

  // Items are already filtered by server, just use them directly
  const filteredItems = items

  const activeFilterCount = [
    !currentStream && filters.streamId,
    filters.sourceType,
    filters.gemStatus !== 'all',
    filters.reportStatus,
    filters.dateFrom || filters.dateTo,
  ].filter(Boolean).length

  const clearFilters = () => {
    updateFilters(() => ({
      streamId: currentStream?.id || null,
      sourceType: null,
      gemStatus: 'all',
      reportStatus: null,
      dateFrom: null,
      dateTo: null,
      search: '',
    }), true)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    })
  }

  const SourceIcon = {
    email: Mail,
    webhook: Webhook,
    csv: FileSpreadsheet,
  }

  return (
    <div className="min-h-screen bg-stone-50 dark:bg-stone-950">
      {/* Header */}
      <header className="bg-white dark:bg-stone-900 border-b border-stone-200 dark:border-stone-800">
        <div className="max-w-5xl mx-auto px-6 py-5">
          {/* Breadcrumb */}
          {currentStream && onBack && (
            <button
              onClick={onBack}
              className="inline-flex items-center gap-1 text-sm text-stone-500 dark:text-stone-400 hover:text-stone-700 dark:hover:text-stone-300 transition-colors mb-4"
            >
              <ChevronLeft className="w-4 h-4" />
              <span>Streams</span>
            </button>
          )}

          <div className="flex items-center justify-between gap-4">
            <div>
              <h1 className="text-xl font-semibold text-stone-900 dark:text-stone-100">
                {currentStream ? `${currentStream.name} Items` : 'All Items'}
              </h1>
              <p className="text-sm text-stone-500 dark:text-stone-400 mt-1">
                {filteredItems.length} of {pagination?.totalCount ?? items.length} item{(pagination?.totalCount ?? items.length) !== 1 ? 's' : ''}
              </p>
            </div>

            <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
              {/* Filter Toggle */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors ${
                  showFilters || activeFilterCount > 0
                    ? 'bg-teal-100 dark:bg-teal-900/40 text-teal-700 dark:text-teal-300'
                    : 'bg-stone-100 dark:bg-stone-800 text-stone-600 dark:text-stone-400 hover:bg-stone-200 dark:hover:bg-stone-700'
                }`}
              >
                <Filter className="w-4 h-4" />
                <span className="hidden sm:inline">Filters</span>
                {activeFilterCount > 0 && (
                  <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-teal-600 text-white text-xs font-medium">
                    {activeFilterCount}
                  </span>
                )}
              </button>

              {/* Upload CSV */}
              <button
                onClick={onUploadCsv}
                className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 text-sm font-medium rounded-lg bg-teal-600 hover:bg-teal-700 text-white transition-colors"
              >
                <Upload className="w-4 h-4" />
                <span className="hidden sm:inline">Upload CSV</span>
                <span className="sm:hidden">Upload</span>
              </button>
            </div>
          </div>

          {/* Search */}
          <div className="relative mt-4 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400 dark:text-stone-500" />
            <input
              type="text"
              placeholder="Search items..."
              value={filters.search}
              onChange={(e) =>
                updateFilters((prev) => ({ ...prev, search: e.target.value }))
              }
              className="w-full pl-9 pr-4 py-2 text-sm bg-stone-100 dark:bg-stone-800 border border-transparent focus:border-teal-500 dark:focus:border-teal-400 rounded-lg text-stone-900 dark:text-stone-100 placeholder-stone-400 dark:placeholder-stone-500 outline-none transition-colors"
            />
            {filters.search && (
              <button
                onClick={() => updateFilters((prev) => ({ ...prev, search: '' }), true)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600 dark:text-stone-500 dark:hover:text-stone-300"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Filters Panel */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-stone-200 dark:border-stone-800">
              <div className="flex flex-wrap items-end gap-4">
                {/* Stream Filter (only when not in stream context) */}
                {!currentStream && streams.length > 0 && (
                  <div className="flex-1 min-w-[160px] max-w-[200px]">
                    <label className="block text-xs font-medium text-stone-500 dark:text-stone-400 mb-1.5">
                      Stream
                    </label>
                    <div className="relative">
                      <select
                        value={filters.streamId || ''}
                        onChange={(e) =>
                          updateFilters((prev) => ({
                            ...prev,
                            streamId: e.target.value || null,
                          }), true)
                        }
                        className="w-full appearance-none px-3 py-2 pr-8 text-sm bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-lg text-stone-900 dark:text-stone-100 outline-none focus:border-teal-500 dark:focus:border-teal-400 transition-colors"
                      >
                        <option value="">All streams</option>
                        {streams.map((stream) => (
                          <option key={stream.id} value={stream.id}>
                            {stream.name}
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400 dark:text-stone-500 pointer-events-none" />
                    </div>
                  </div>
                )}

                {/* Source Type Filter */}
                <div className="flex-1 min-w-[140px] max-w-[160px]">
                  <label className="block text-xs font-medium text-stone-500 dark:text-stone-400 mb-1.5">
                    Source
                  </label>
                  <div className="relative">
                    <select
                      value={filters.sourceType || ''}
                      onChange={(e) =>
                        updateFilters((prev) => ({
                          ...prev,
                          sourceType: (e.target.value as SourceType) || null,
                        }), true)
                      }
                      className="w-full appearance-none px-3 py-2 pr-8 text-sm bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-lg text-stone-900 dark:text-stone-100 outline-none focus:border-teal-500 dark:focus:border-teal-400 transition-colors"
                    >
                      <option value="">All sources</option>
                      <option value="email">Email</option>
                      <option value="webhook">Webhook</option>
                      <option value="csv">CSV</option>
                    </select>
                    <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400 dark:text-stone-500 pointer-events-none" />
                  </div>
                </div>

                {/* Gem Status Filter */}
                <div className="flex-1 min-w-[140px] max-w-[160px]">
                  <label className="block text-xs font-medium text-stone-500 dark:text-stone-400 mb-1.5">
                    Gem Status
                  </label>
                  <div className="relative">
                    <select
                      value={filters.gemStatus}
                      onChange={(e) =>
                        updateFilters((prev) => ({
                          ...prev,
                          gemStatus: e.target.value as GemFilter,
                        }), true)
                      }
                      className="w-full appearance-none px-3 py-2 pr-8 text-sm bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-lg text-stone-900 dark:text-stone-100 outline-none focus:border-teal-500 dark:focus:border-teal-400 transition-colors"
                    >
                      <option value="all">All items</option>
                      <option value="gems">Gems only</option>
                      <option value="not-gems">Not gems</option>
                    </select>
                    <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400 dark:text-stone-500 pointer-events-none" />
                  </div>
                </div>

                {/* Report Status Filter */}
                <div className="flex-1 min-w-[140px] max-w-[180px]">
                  <label className="block text-xs font-medium text-stone-500 dark:text-stone-400 mb-1.5">
                    Report Status
                  </label>
                  <div className="relative">
                    <select
                      value={filters.reportStatus || ''}
                      onChange={(e) =>
                        updateFilters((prev) => ({
                          ...prev,
                          reportStatus: (e.target.value as ReportStatus) || null,
                        }), true)
                      }
                      className="w-full appearance-none px-3 py-2 pr-8 text-sm bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-lg text-stone-900 dark:text-stone-100 outline-none focus:border-teal-500 dark:focus:border-teal-400 transition-colors"
                    >
                      <option value="">All</option>
                      <option value="processed">Processed</option>
                      <option value="pending">Pending</option>
                      <option value="unprocessed">Unprocessed</option>
                    </select>
                    <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400 dark:text-stone-500 pointer-events-none" />
                  </div>
                </div>

                {/* Date From */}
                <div className="flex-1 min-w-[130px] max-w-[150px]">
                  <label className="block text-xs font-medium text-stone-500 dark:text-stone-400 mb-1.5">
                    From
                  </label>
                  <input
                    type="date"
                    value={filters.dateFrom || ''}
                    onChange={(e) =>
                      updateFilters((prev) => ({
                        ...prev,
                        dateFrom: e.target.value || null,
                      }), true)
                    }
                    className="w-full px-3 py-2 text-sm bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-lg text-stone-900 dark:text-stone-100 outline-none focus:border-teal-500 dark:focus:border-teal-400 transition-colors"
                  />
                </div>

                {/* Date To */}
                <div className="flex-1 min-w-[130px] max-w-[150px]">
                  <label className="block text-xs font-medium text-stone-500 dark:text-stone-400 mb-1.5">
                    To
                  </label>
                  <input
                    type="date"
                    value={filters.dateTo || ''}
                    onChange={(e) =>
                      updateFilters((prev) => ({
                        ...prev,
                        dateTo: e.target.value || null,
                      }), true)
                    }
                    className="w-full px-3 py-2 text-sm bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-lg text-stone-900 dark:text-stone-100 outline-none focus:border-teal-500 dark:focus:border-teal-400 transition-colors"
                  />
                </div>

                {activeFilterCount > 0 && (
                  <button
                    onClick={clearFilters}
                    className="px-3 py-2 text-sm text-stone-500 dark:text-stone-400 hover:text-stone-700 dark:hover:text-stone-300 transition-colors"
                  >
                    Clear all
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Items List */}
      <main className="max-w-5xl mx-auto px-6 py-6">
        {filteredItems.length === 0 ? (
          <div className="text-center py-16">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-stone-100 dark:bg-stone-800 mb-4">
              <Inbox className="w-6 h-6 text-stone-400 dark:text-stone-500" />
            </div>
            <p className="text-stone-600 dark:text-stone-400 font-medium">
              No items found
            </p>
            <p className="text-sm text-stone-500 dark:text-stone-500 mt-1">
              {activeFilterCount > 0 || filters.search
                ? 'Try adjusting your filters or search'
                : 'Items will appear here when data comes in'}
            </p>
          </div>
        ) : (
          <>
            <div className="space-y-2">
              {filteredItems.map((item) => (
                <ItemRow
                  key={item.id}
                  item={item}
                  showStream={!currentStream}
                  onView={() => onView?.(item.id)}
                  onFlagAsGem={() => onFlagAsGem?.(item.id)}
                  onUnflagGem={() => onUnflagGem?.(item.id)}
                  onDelete={() => onDelete?.(item.id)}
                  formatDate={formatDate}
                  formatTime={formatTime}
                  SourceIcon={SourceIcon}
                />
              ))}
            </div>

            {pagination && onPageChange && (
              <Pagination
                currentPage={pagination.currentPage}
                totalPages={pagination.totalPages}
                totalCount={pagination.totalCount}
                perPage={pagination.perPage}
                onPageChange={(page) => onPageChange(page, toFilterParams(filters))}
              />
            )}
          </>
        )}
      </main>
    </div>
  )
}

// =============================================================================
// Item Row
// =============================================================================

interface ItemRowProps {
  item: Item
  showStream: boolean
  onView?: () => void
  onFlagAsGem?: () => void
  onUnflagGem?: () => void
  onDelete?: () => void
  formatDate: (date: string) => string
  formatTime: (date: string) => string
  SourceIcon: Record<SourceType, typeof Mail>
}

function ItemRow({
  item,
  showStream,
  onView,
  onFlagAsGem,
  onUnflagGem,
  onDelete,
  formatDate,
  formatTime,
  SourceIcon,
}: ItemRowProps) {
  const Icon = SourceIcon[item.sourceType]
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false)
      }
    }
    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [menuOpen])

  const handleMenuClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    setMenuOpen(!menuOpen)
  }

  const handleAction = (action: () => void | undefined) => (e: React.MouseEvent) => {
    e.stopPropagation()
    setMenuOpen(false)
    action?.()
  }

  return (
    <div
      onClick={onView}
      className="w-full text-left group rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900/50 p-4 hover:border-teal-300 dark:hover:border-teal-700 hover:shadow-sm transition-all cursor-pointer"
    >
      <div className="flex items-start gap-4">
        {/* Source Icon */}
        <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-stone-100 dark:bg-stone-800 flex items-center justify-center group-hover:bg-teal-100 dark:group-hover:bg-teal-900/40 transition-colors">
          <Icon className="w-4 h-4 text-stone-500 dark:text-stone-400 group-hover:text-teal-600 dark:group-hover:text-teal-400 transition-colors" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-medium text-stone-900 dark:text-stone-100 truncate group-hover:text-teal-600 dark:group-hover:text-teal-400 transition-colors">
              {item.subject}
            </h3>
            {item.rejected && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300">
                <AlertTriangle className="w-3 h-3" />
                <span className="text-xs font-medium">Rejected</span>
              </span>
            )}
            {item.isGem && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300">
                <Sparkles className="w-3 h-3" />
                <span className="text-xs font-medium">Gem</span>
              </span>
            )}
          </div>

          <p className="text-sm text-stone-500 dark:text-stone-400 line-clamp-2 mb-2">
            {item.contentPreview}
          </p>

          {/* Desktop: inline metadata */}
          <div className="hidden sm:flex items-center gap-3 text-xs text-stone-400 dark:text-stone-500">
            {showStream && (
              <>
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-teal-50 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400 font-medium">
                  {item.streamName}
                </span>
                <span className="text-stone-300 dark:text-stone-700">•</span>
              </>
            )}
            <span className="capitalize">{item.sourceType}</span>
            <span className="text-stone-300 dark:text-stone-700">•</span>
            <span className="flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              {formatDate(item.receivedAt)} at {formatTime(item.receivedAt)}
            </span>
          </div>

          {/* Mobile: stacked metadata */}
          <div className="sm:hidden flex flex-wrap items-center gap-2 text-xs text-stone-400 dark:text-stone-500">
            {showStream && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-teal-50 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400 font-medium">
                {item.streamName}
              </span>
            )}
            <span className="capitalize">{item.sourceType}</span>
            <span className="text-stone-300 dark:text-stone-700">•</span>
            <span className="flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              {formatDate(item.receivedAt)}
            </span>
          </div>
        </div>

        {/* Action Menu */}
        <div ref={menuRef} className="relative flex-shrink-0">
          <button
            onClick={handleMenuClick}
            className="p-1.5 rounded-lg text-stone-400 hover:text-stone-600 dark:text-stone-500 dark:hover:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-800 transition-colors"
          >
            <MoreVertical className="w-4 h-4" />
          </button>

          {menuOpen && (
            <div className="absolute right-0 top-full mt-1 w-44 py-1 bg-white dark:bg-stone-800 rounded-lg shadow-lg border border-stone-200 dark:border-stone-700 z-10">
              <button
                onClick={handleAction(onView)}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-stone-700 dark:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-700"
              >
                <Eye className="w-4 h-4" />
                View
              </button>

              {item.isGem ? (
                <button
                  onClick={handleAction(onUnflagGem)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-stone-700 dark:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-700"
                >
                  <Flag className="w-4 h-4" />
                  Unflag as Gem
                </button>
              ) : (
                <button
                  onClick={handleAction(onFlagAsGem)}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-amber-600 dark:text-amber-400 hover:bg-stone-100 dark:hover:bg-stone-700"
                >
                  <Flag className="w-4 h-4" />
                  Flag as Gem
                </button>
              )}

              {item.relatedReports.length > 0 && (
                <a
                  href={`/reports/${item.relatedReports[0].id}`}
                  onClick={(e) => e.stopPropagation()}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-stone-700 dark:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-700"
                >
                  <FileText className="w-4 h-4" />
                  View Report
                </a>
              )}

              <div className="border-t border-stone-200 dark:border-stone-700 my-1" />

              <button
                onClick={handleAction(onDelete)}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-stone-100 dark:hover:bg-stone-700"
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
